/**
 * useSavedMaterials Hook
 * ======================
 * Manages saved materials state across the app.
 * Works in both DEMO MODE (localStorage) and AUTH MODE (Supabase).
 * Provides toggle functionality for save/unsave actions.
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  getSavedMaterials, 
  isMaterialSaved, 
  toggleSaveMaterial 
} from '../services/workspaceService';

/**
 * Hook to manage a single material's saved state
 * Works without authentication (demo mode uses localStorage)
 * @param {string} materialId - Material ID
 * @param {Object} materialData - Material metadata for saving
 * @returns {Object} { isSaved, isLoading, toggle }
 */
export const useMaterialSaveState = (materialId, materialData = {}) => {
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  // Check initial saved state
  useEffect(() => {
    if (!materialId) {
      setIsSaved(false);
      return;
    }
    
    const checkSaved = async () => {
      const saved = await isMaterialSaved(materialId);
      setIsSaved(saved);
    };
    
    checkSaved();
  }, [materialId]);
  
  // Toggle save state
  const toggle = useCallback(async () => {
    if (isLoading) return { success: false };
    
    setIsLoading(true);
    const result = await toggleSaveMaterial(materialId, materialData);
    
    if (result.success) {
      setIsSaved(result.isSaved);
    }
    
    setIsLoading(false);
    return result;
  }, [isLoading, materialId, materialData]);
  
  return { isSaved, isLoading, toggle };
};

/**
 * Hook to manage all saved materials
 * Works without authentication (demo mode uses localStorage)
 * @returns {Object} { savedMaterials, isLoading, refresh, isSaved, toggle }
 */
export const useSavedMaterials = () => {
  const [savedMaterials, setSavedMaterials] = useState([]);
  const [savedIds, setSavedIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  
  // Load saved materials
  const refresh = useCallback(async () => {
    setIsLoading(true);
    const materials = await getSavedMaterials();
    setSavedMaterials(materials);
    setSavedIds(new Set(materials.map(m => m.material_id)));
    setIsLoading(false);
  }, []);
  
  // Load on mount
  useEffect(() => {
    refresh();
  }, [refresh]);
  
  // Check if material is saved
  const isSaved = useCallback((materialId) => {
    return savedIds.has(materialId);
  }, [savedIds]);
  
  // Toggle save for a material
  const toggle = useCallback(async (materialId, materialData = {}) => {
    const result = await toggleSaveMaterial(materialId, materialData);
    
    if (result.success) {
      // Update local state
      if (result.isSaved) {
        setSavedIds(prev => new Set([...prev, materialId]));
        setSavedMaterials(prev => [...prev, { material_id: materialId, ...materialData }]);
      } else {
        setSavedIds(prev => {
          const next = new Set(prev);
          next.delete(materialId);
          return next;
        });
        setSavedMaterials(prev => prev.filter(m => m.material_id !== materialId));
      }
    }
    
    return result;
  }, []);
  
  return { 
    savedMaterials, 
    isLoading, 
    refresh, 
    isSaved, 
    toggle,
    count: savedMaterials.length
  };
};

export default useSavedMaterials;
